import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import keycloak from '../services/keycloak/keycloak';
import LoadingScreen from '../layout/LoadingScreen';

const KeycloakContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Decode a JWT payload without validation
const decodeJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const ensureUserProfile = async (userId, token) => {
  try {
    const response = await fetch(`${API_URL}/profile/ensure`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw {
        status: response.status,
        message: errorData.message || 'Failed to ensure user profile',
        code: errorData.code,
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Error ensuring user profile:', error);
    throw error;
  }
};

// Initialize session tracking in the right storage
const initSessionTracking = (storage) => {
  const now = Date.now().toString();
  storage.setItem('session_start', now);
  storage.setItem('last_activity', now);
};

export const useAuth = () => {
  const context = useContext(KeycloakContext);
  if (!context) throw new Error('useAuth must be used within KeycloakProvider');
  return context;
};

export const KeycloakProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const isInitializingRef = useRef(false);

  useEffect(() => {
    if (isInitializingRef.current) return;
    isInitializingRef.current = true;

    const initKeycloak = async () => {
      try {
        // 1. Check for OAuth callback code in URL first
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const errorParam = urlParams.get('error');

        if (errorParam) {
          console.error(
            '🔍 DEBUG: OAuth error in URL:',
            urlParams.get('error_description') || errorParam
          );
          window.location.href = '/login';
          return;
        }

        if (code) {
          // Clean URL immediately to prevent double processing
          window.history.replaceState({}, document.title, window.location.origin);

          const response = await fetch(`${API_URL}/auth/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: code, redirect_uri: window.location.origin }),
          });

          if (response.ok) {
            const tokens = await response.json();
            if (tokens.access_token) {
              keycloak.token = tokens.access_token;
              keycloak.refreshToken = tokens.refresh_token;
              keycloak.idToken = tokens.id_token;
              keycloak.authenticated = true;
              keycloak.tokenParsed = decodeJwt(tokens.access_token);

              const expiresIn = tokens.expires_in || 300;
              keycloak.startTokenRefresh(expiresIn);

              // Store in sessionStorage + init session tracking
              sessionStorage.setItem(
                'keycloak_tokens',
                JSON.stringify({
                  access_token: tokens.access_token,
                  refresh_token: tokens.refresh_token,
                  id_token: tokens.id_token,
                  expires_at: Date.now() + expiresIn * 1000,
                })
              );
              initSessionTracking(sessionStorage);
              initSessionTracking(localStorage); // Keep both in sync for simple restores

              const userId = keycloak.tokenParsed?.sub;
              if (userId) {
                try {
                  const ensureResult = await ensureUserProfile(userId, tokens.access_token);
                  // If the backend generated an autoUsername (firstname_lastname),
                  // patch the in-memory tokenParsed immediately so App.js
                  // picks it up now — without waiting for a token refresh.
                  if (ensureResult?.username && keycloak.tokenParsed) {
                    keycloak.tokenParsed.preferred_username = ensureResult.username;
                  }
                } catch (ensureError) {
                  if (ensureError.status === 403 || ensureError.code === 'CROSS_AUTH_DISALLOWED') {
                    console.error('❌ Account disjointness violation:', ensureError.message);

                    // Clear all tokens and storage because they are logged in to Keycloak
                    // but our app refuses this login method.
                    keycloak.token = null;
                    keycloak.refreshToken = null;
                    keycloak.authenticated = false;
                    sessionStorage.clear();
                    localStorage.removeItem('keycloak_tokens');

                    // Redirect to login with the specific error message
                    window.location.href = `/login?message=${encodeURIComponent(ensureError.message)}`;
                    return;
                  }
                  // For other errors, we might still want to proceed or allow setAuthenticated(true)
                  // depending on criticality. For now, let's log and continue if not disjointness error.
                  console.error('Error during ensureUser (OAuth callback):', ensureError);
                }
              }

              setAuthenticated(true);
              setInitialized(true);
              if (userId) sessionStorage.setItem(`profile_ensured_${userId}`, 'true');
              return;
            }
          } else {
            const err = await response.json().catch(() => ({}));
            console.error('❌ OAuth callback failed:', err);
          }
        }

        // 2. Try to restore existing session from storage
        const storedTokens =
          localStorage.getItem('keycloak_tokens') || sessionStorage.getItem('keycloak_tokens');
        const rememberMe = localStorage.getItem('remember_me') === 'true';
        const isFromSessionStorage = !!sessionStorage.getItem('keycloak_tokens');

        if (storedTokens && (rememberMe || isFromSessionStorage)) {
          try {
            const tokens = JSON.parse(storedTokens);
            const refreshResponse = await fetch(`${API_URL}/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: tokens.refresh_token }),
            });

            if (refreshResponse.ok) {
              const refreshedTokens = await refreshResponse.json();
              if (refreshedTokens.access_token) {
                keycloak.token = refreshedTokens.access_token;
                keycloak.refreshToken = refreshedTokens.refresh_token;
                keycloak.idToken = refreshedTokens.id_token;
                keycloak.authenticated = true;
                keycloak.tokenParsed = decodeJwt(refreshedTokens.access_token);

                const expiresIn = refreshedTokens.expires_in || 300;
                keycloak.startTokenRefresh(expiresIn);

                const storage = isFromSessionStorage ? sessionStorage : localStorage;
                storage.setItem(
                  'keycloak_tokens',
                  JSON.stringify({
                    access_token: refreshedTokens.access_token,
                    refresh_token: refreshedTokens.refresh_token,
                    id_token: refreshedTokens.id_token,
                    expires_at: Date.now() + expiresIn * 1000,
                  })
                );

                // Ensure session tracking is initialized
                if (!storage.getItem('session_start')) {
                  initSessionTracking(storage);
                }
                storage.setItem('last_activity', Date.now().toString());

                const userId = keycloak.tokenParsed?.sub;
                if (userId) {
                  try {
                    const ensureResult = await ensureUserProfile(
                      userId,
                      refreshedTokens.access_token
                    );
                    if (ensureResult?.autoUsername && keycloak.tokenParsed) {
                      keycloak.tokenParsed.preferred_username = ensureResult.autoUsername;
                    }
                  } catch (ensureError) {
                    if (
                      ensureError.status === 403 ||
                      ensureError.code === 'CROSS_AUTH_DISALLOWED'
                    ) {
                      console.error(
                        '❌ Account disjointness violation during restore:',
                        ensureError.message
                      );
                      localStorage.removeItem('keycloak_tokens');
                      sessionStorage.removeItem('keycloak_tokens');
                      window.location.href = `/login?message=${encodeURIComponent(ensureError.message)}`;
                      return;
                    }
                    console.error('Error during ensureUser (session restore):', ensureError);
                  }
                }

                setAuthenticated(true);
                setInitialized(true);
                if (userId) sessionStorage.setItem(`profile_ensured_${userId}`, 'true');
                return;
              }
            } else {
              console.warn('❌ Token refresh failed with status:', refreshResponse.status);
              const errorText = await refreshResponse.text();
              console.error('🔍 DEBUG: Refresh error response:', errorText);
              console.warn('❌ Token refresh failed, clearing stored session');
              localStorage.removeItem('keycloak_tokens');
              sessionStorage.removeItem('keycloak_tokens');
            }
          } catch (e) {
            console.error('Error during session restore:', e);
            localStorage.removeItem('keycloak_tokens');
            sessionStorage.removeItem('keycloak_tokens');
          }
        }

        // 3. No valid session found
        setAuthenticated(false);
        setInitialized(true);
      } catch (error) {
        console.error('Keycloak init error:', error);
        setAuthenticated(false);
        setInitialized(true);
      }
    };

    initKeycloak();
  }, []);

  // Keep React state in sync with keycloak object changes
  // Listen for the immediate auth-success event from authService.login()
  useEffect(() => {
    const handleAuthSuccess = () => {
      if (keycloak.authenticated && !authenticated) {
        setAuthenticated(true);
        if (keycloak.tokenParsed) {
          const tp = keycloak.tokenParsed;
          const fullName =
            tp.name ||
            [tp.given_name, tp.family_name].filter(Boolean).join(' ') ||
            tp.preferred_username ||
            '';
          setUser({
            id: tp.sub,
            username: tp.preferred_username,
            email: tp.email,
            name: fullName,
            fullName: fullName,
            firstName: tp.given_name || '',
            lastName: tp.family_name || '',
          });
        }
      }
    };

    // Register both callback and event listener
    keycloak.onAuthSuccess = handleAuthSuccess;
    window.addEventListener('keycloak-auth-success', handleAuthSuccess);

    // Fallback polling (slower interval) for edge cases
    const syncInterval = setInterval(() => {
      if (keycloak.authenticated && !authenticated) {
        setAuthenticated(true);
        if (keycloak.tokenParsed && !user) {
          const tp = keycloak.tokenParsed;
          const fullName =
            tp.name ||
            [tp.given_name, tp.family_name].filter(Boolean).join(' ') ||
            tp.preferred_username ||
            '';
          setUser({
            id: tp.sub,
            username: tp.preferred_username,
            email: tp.email,
            name: fullName,
            fullName: fullName,
            firstName: tp.given_name || '',
            lastName: tp.family_name || '',
          });
        }
      }

      // FALLBACK: If authenticated but no profile_ensured flag for THIS user, try to ensure now
      const userId = keycloak.tokenParsed?.sub;
      if (
        keycloak.authenticated &&
        userId &&
        !sessionStorage.getItem(`profile_ensured_${userId}`)
      ) {
        if (keycloak.token) {
          ensureUserProfile(userId, keycloak.token)
            .then(() => sessionStorage.setItem(`profile_ensured_${userId}`, 'true'))
            .catch((err) => console.error('Fallback ensure failed:', err));
        }
      }
    }, 3000);

    return () => {
      keycloak.onAuthSuccess = null;
      window.removeEventListener('keycloak-auth-success', handleAuthSuccess);
      clearInterval(syncInterval);
    };
  }, [authenticated, user]);

  // SSE: Listen for real-time force-logout events from the backend.
  // When admin deletes this user, the backend pushes a "force-logout" event
  // through the SSE connection — truly instant, no polling.
  useEffect(() => {
    if (!authenticated || !keycloak.token) return;

    const sseUrl = `${API_URL}/auth/session-events?token=${encodeURIComponent(keycloak.token)}`;
    const eventSource = new EventSource(sseUrl);

    eventSource.addEventListener('connected', (e) => {});

    eventSource.addEventListener('force-logout', (e) => {
      console.error('❌ Received force-logout event from server. Logging out...');

      // Close the SSE connection
      eventSource.close();

      // Stop token refresh
      keycloak.stopTokenRefresh();

      // Clear all tokens and storage
      keycloak.token = null;
      keycloak.refreshToken = null;
      keycloak.idToken = null;
      keycloak.tokenParsed = null;
      keycloak.authenticated = false;

      localStorage.removeItem('keycloak_tokens');
      localStorage.removeItem('remember_me');
      localStorage.removeItem('session_expiry');
      localStorage.removeItem('session_start');
      localStorage.removeItem('last_activity');
      sessionStorage.clear();

      // Redirect to login with informative message
      window.location.href =
        '/login?message=' +
        encodeURIComponent(
          'Your account has been removed by an administrator. You have been logged out.'
        );
    });

    eventSource.onerror = () => {
      // SSE errors are normal on disconnect/reconnect — EventSource auto-reconnects
      console.warn('⚠️ SSE connection error (will auto-reconnect)');
    };

    return () => {
      eventSource.close();
    };
  }, [authenticated]);

  const handleLogin = useCallback(() => keycloak.login(), []);
  const handleLogout = useCallback(() => keycloak.logout(), []);
  const handleRegister = useCallback(() => keycloak.register(), []);

  const value = React.useMemo(
    () => ({
      keycloak,
      initialized,
      authenticated,
      user,
      login: handleLogin,
      logout: handleLogout,
      register: handleRegister,
    }),
    [initialized, authenticated, user, handleLogin, handleLogout, handleRegister]
  );

  if (!initialized) {
    return <LoadingScreen fullScreen={true} />;
  }

  return <KeycloakContext.Provider value={value}>{children}</KeycloakContext.Provider>;
};
